import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
  Assay,
  Center,
  Contact,
  DataFile,
  Investigation,
  Material,
  MaterialAttribute,
  MaterialAttributeValue,
  OntologyAnnotation,
  OntologySourceReference,
  Process,
  ProcessParameterValue,
  Publication,
  Sample,
  SearchTerm,
  Source,
  Study,
  StudyFactor,
} from './curation-service.types';
import { map } from 'rxjs/operators';
interface ColumnConfig { header: string; binding: string; isReadOnly?: boolean; isRequired?: boolean; isUnique?: boolean; isEditable?: boolean; unit?: string; order?: number; }
import { FilterTerm, PagedResponse } from '@bioconnect-ui/shared-service';
import { ContextData } from '@bioconnect-ui/file-service';


export interface CurationConfig {
  restServer: string;
  wijmoLicenseKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class CurationService {
  constructor(private httpClient: HttpClient,
    @Inject('curationServiceConfig') private curationServiceConfig: CurationConfig) { }

  private restServer = this.curationServiceConfig.restServer;

  getAllInvestigations(): Observable<Investigation[]> {
    return this.httpClient.get<Investigation[]>(
      `${this.restServer}/metadata_repository/investigation/get_list/?page_size=100`
    );
  }

  getInvestigations(pageNumber: number, pageSize:number, filter: string): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/investigation/?page=${pageNumber}&page_size=${pageSize}&filter=${filter}`
    );
  }

  getInvestigation(id: number): Observable<Investigation> {
    return this.httpClient.get<Investigation>(
      `${this.restServer}/metadata_repository/investigation/${id}/`
    );
  }

  getInvestigationSummary(id: number): Observable<Investigation> {
    return this.httpClient.get<Investigation>(
      `${this.restServer}/metadata_repository/investigation/summary/${id}/`
    );
  }

  addInvestigation(investigation: Investigation): Observable<Investigation> {
    return this.httpClient.post<Investigation>(
      `${this.restServer}/metadata_repository/investigation/`,
      investigation
    );
  }

  updateInvestigation(investigation: Investigation): Observable<Investigation> {
    return this.httpClient.patch<Investigation>(
      `${this.restServer}/metadata_repository/investigation/${investigation.id}/`,
      investigation
    );
  }

  deleteInvestigation(id: number): Observable<Investigation> {
    return this.httpClient.delete<Investigation>(
      `${this.restServer}/metadata_repository/investigation/${id}/`
    );
  }

  getStudyCount(): Observable<number> {
    return this.httpClient.get<number>(
      `${this.restServer}/metadata_repository/study/count/`
    );
  }

  getStudies(pageNumber: number, pageSize: number, filter: string, filterTerms: string, 
    contactTerms: string, factorValueTerms: string, permission="can_view"): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/study/?page=${pageNumber}&page_size=${pageSize}` +
      `&filter=${encodeURIComponent(filter)}&filter_terms=${filterTerms}&contact_terms=${contactTerms}&factor_value_terms=${factorValueTerms}&permission=${permission}`
    );
  }

  getStudiesByOrigin(originId: number, pageNumber: number, pageSize: number): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/study/by_origin/${originId}/?page=${pageNumber}&page_size=${pageSize}`
    );
  }

  getAllStudies(): Observable<Study[]> {
    return this.httpClient.get(
      `${this.restServer}/metadata_repository/study/list/`
    ).pipe(map((result) => <Study[]>result));
  }

  getLatestStudies(limit: number): Observable<Study[]> {
    return this.httpClient.get<Study[]>(
      `${this.restServer}/metadata_repository/study/latest_studies/${limit}/`
    );
  }

  getStudy(id: number): Observable<Study> {
    return this.httpClient.get<Study>(
      `${this.restServer}/metadata_repository/study/${id}/details/`
    );
  }

  addStudy(study: Study): Observable<Study> {
    return this.httpClient.post<Study>(
      `${this.restServer}/metadata_repository/study/`,
      study
    );
  }

  updateStudy(study: Study): Observable<Study> {
    return this.httpClient.patch<Study>(
      `${this.restServer}/metadata_repository/study/${study.id}/`,
      study
    );
  }

  deleteStudy(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/study/${id}/`
    );
  }

  getAssayCount(): Observable<number> {
    return this.httpClient.get<number>(
      `${this.restServer}/metadata_repository/assay/count/`
    );
  }

  getAssays(pageNumber: number, filter: string): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/assay/?page=${pageNumber}&filter=${filter}`
    );
  }

  getAllAssays(): Observable<Assay[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/assay/?page_size=1000`
    ).pipe(map((result) => <Assay[]>result.results));
  }


  getAssaysByStudy(studyId: number): Observable<Assay[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/assay/?study_id=${studyId}`
    ).pipe(map((result) => <Assay[]>result.results));
  }

  getAssayPreviewByStudy(studyId: number): Observable<Assay[]> {
    return this.httpClient.get<Assay[]>(
      `${this.restServer}/metadata_repository/assay/preview_by_study/${studyId}/`
    ).pipe(map((result) => <Assay[]>result));
  }

  getAssaysByOrigin(originId: number, pageNumber: number, pageSize: number): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/assay/by_origin/${originId}/?page=${pageNumber}&page_size=${pageSize}`
    );
  }

  getAssay(id: number): Observable<Assay> {
    return this.httpClient.get<Assay>(
      `${this.restServer}/metadata_repository/assay/${id}/details/`
    );
  }

  addAssay(assay: Assay): Observable<Assay> {
    return this.httpClient.post<Assay>(
      `${this.restServer}/metadata_repository/assay/`,
      assay
    );
  }

  updateAssay(assay: Assay): Observable<Assay> {
    return this.httpClient.patch<Assay>(
      `${this.restServer}/metadata_repository/assay/${assay.id}/`,
      assay
    );
  }

  deleteAssay(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/assay/${id}/`
    );
  }

  getContact(id: number): Observable<Contact> {
    return this.httpClient.get<Contact>(
      `${this.restServer}/metadata_repository/contact/${id}/`
    );
  }

  getContacts(): Observable<Contact[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/contact/?page_size=500`
    ).pipe(map((result) => <Contact[]>result.results))
  }

  addContact(contact: Contact): Observable<Contact> {
    return this.httpClient.post<Contact>(
      `${this.restServer}/metadata_repository/contact/`,
      contact
    );
  }

  deleteContact(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/contact/${id}/`
    )
  }

  updateContact(contact: Contact): Observable<Contact> {
    return this.httpClient.patch<Contact>(
      `${this.restServer}/metadata_repository/contact/${contact.id}/`,
      contact
    );
  }

  getDataCount(): Observable<number> {
    return this.httpClient.get<number>(
      `${this.restServer}/metadata_repository/data/count/`
    );
  }

  getDataById(id: number): Observable<DataFile> {
    return this.httpClient.get<DataFile>(
      `${this.restServer}/metadata_repository/data/${id}/`
    );
  }

  getDataByAssay(id: number, pageSize: number): Observable<DataFile[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/data/by_assay/${id}/?page_size=${pageSize}`
    ).pipe(map((result) => <DataFile[]>result.results))
  }

  getPagedDataByAssay(id: number, pageNumber: number, page_size: number): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/metadata_repository/data/by_assay/${id}/?page=${pageNumber}&page_size=${page_size}`)
      .pipe(map(result => <PagedResponse>result));
  }

  getPublications(filter: string): Observable<Publication[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/publication/?page_size=500&filter=${filter}`
    ).pipe(map((result) => <Publication[]>result.results))
  }

  getPublication(id: number): Observable<Publication> {
    return this.httpClient.get<Publication>(
      `${this.restServer}/metadata_repository/publication/${id}/`
    );
  }

  addPublication(publication: Publication): Observable<Publication> {
    return this.httpClient.post<Publication>(
      `${this.restServer}/metadata_repository/publication/`,
      publication
    );
  }

  updatePublication(publication: Publication): Observable<Publication> {
    return this.httpClient.patch<Publication>(
      `${this.restServer}/metadata_repository/publication/${publication.id}/`,
      publication
    );
  }

  deletePublication(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/publication/${id}/`
    );
  }

  getSample(id: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.restServer}/metadata_repository/sample/${id}/`
    );
  }

  getSamplesByAssay(id: number): Observable<Sample[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/sample/by_assay/${id}/?page_size=20000`
    ).pipe(map((result) => <Sample[]>result.results))
  }

  getSampleNamesByAssay(id: number): Observable<Sample[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/sample/names_by_assay/${id}/?page_size=20000`
    ).pipe(map((result) => <Sample[]>result.results))
  }

  getPagedSamplesByAssay(id: number, pageNumber: number, page_size: number, sort_by?: string, sort_direction?: string): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/metadata_repository/sample/by_assay/${id}/?page=${pageNumber}&page_size=${page_size}&sort=${sort_by}&direction=${sort_direction}`)
      .pipe(map(result => <PagedResponse>result));
  }

  getSampleIdsByAssay(id: number): Observable<number[]> {
    return this.httpClient.get<number[]>(
      `${this.restServer}/metadata_repository/sample/ids_by_assay/${id}/`
    )
  }

  getAllFiles(): Observable<DataFile[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/data/?page_size=1000`
    ).pipe(map((result) => <DataFile[]>result.results));
  }

  addFile(file: DataFile): Observable<DataFile> {
    return this.httpClient.post<DataFile>(
      `${this.restServer}/metadata_repository/data/`,
      file,
      { headers: { 'skip-error-interceptor': 'true' }}
    );
  }

  updateFile(file: DataFile): Observable<DataFile> {
    return this.httpClient.patch<DataFile>(
      `${this.restServer}/metadata_repository/data/${file.id}/`,
      file,
      { headers: { 'skip-error-interceptor': 'true' }}
    );
  }

  updateContextData(contextData: ContextData): Observable<ContextData> {
    return this.httpClient.patch<ContextData>(
      `${this.restServer}/metadata_repository/context_data/${contextData.id}/`,
      contextData,
      { headers: { 'skip-error-interceptor': 'true' }}
    );
  }

  addSampleBatch(samples: Sample[]): Observable<Sample[]> {
    return this.httpClient.post<Sample[]>(
      `${this.restServer}/metadata_repository/sample/get_ids_by_name/`,
      samples
    );
  }

  addDataBatch(data: DataFile[]): Observable<DataFile[]> {
    return this.httpClient.post<DataFile[]>(
      `${this.restServer}/metadata_repository/data/get_ids_by_name/`,
      data
    );
  }

  deleteSampleBatch(ids: number[]) {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/sample/delete_by_ids/`,
      ids
    );
  }

  removeDataFromAssay(data_file_ids: number[], assay_id: number) {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/data/remove_from_assay/`,
      { data_file_ids: data_file_ids, assay_id: assay_id }
    );
  }

  updateSamples(samples: Sample[], assayId: number, originId: number, columnsForUpdate: ColumnConfig[]): Observable<Sample[]> {
    if (samples.length) {
      return this.httpClient.post(
        `${this.restServer}/metadata_repository/sample/batch_update_by_assay/?assay_id=${assayId}&origin_id=${originId}`,
        { samples, columns: columnsForUpdate }
      ).pipe(map((result) => <Sample[]>result))
    } else {
      return of([]);
    }
  }

  updateSamplesFromFile(fileName: string, assayId: number, deleteExisting: boolean, columnsForUpdate: ColumnConfig[]): Observable<string> {
    const deleteExistingArg = deleteExisting ? '&delete_existing=true' : '';
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/sample/batch_update_by_assay_from_file/?assay_id=${assayId}&file_name=${fileName}${deleteExistingArg}`,
      { columns: columnsForUpdate },
      { headers: { 'skip-error-interceptor': 'true' }}
    ).pipe(map((result) => <string>result));
  }

  removeSamplesFromAssay(sample_ids: number[], assay_id: number) {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/sample/remove_from_assay/`,
      { sample_ids: sample_ids, assay_id: assay_id }
    );
  }

  getMaterial(id: number): Observable<Material> {
    return this.httpClient.get<Material>(
      `${this.restServer}/metadata_repository/material/${id}/`
    );
  }

  addMaterial(material: Material): Observable<Material> {
    return this.httpClient.post<Material>(
      `${this.restServer}/metadata_repository/material/`,
      material
    );
  }

  deleteMaterial(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/metadata_repository/material/${id}/`
    );
  }

  getMaterialsByAssay(id: number): Observable<Material[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/material/by_assay/${id}/`
    ).pipe(map((result) => <Material[]>result.results))
  }

  getPagedMaterialsByAssay(id: number, pageNumber: number, page_size: number): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/metadata_repository/material/by_assay/${id}/?page=${pageNumber}&page_size=${page_size}`)
      .pipe(map(result => <PagedResponse>result));
  }

  getSource(id: number): Observable<Source> {
    return this.httpClient.get<Source>(
      `${this.restServer}/metadata_repository/source/${id}/`
    );
  }

  getSourcesByAssay(id: number): Observable<Source[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/source/by_assay/${id}/`
    ).pipe(map((result) => <Source[]>result.results))
  }

  getPagedSourcesByStudy(id: number, page: number, pageSize: number, sort_by?: string, sort_direction?: string): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/source/by_study/${id}/?page=${page}&page_size=${pageSize}&sort=${sort_by}&direction=${sort_direction}`
    ).pipe(map((result) => <PagedResponse>result))
  }

  getSourceIdsByStudy(id: number): Observable<number[]> {
    return this.httpClient.get<number[]>(
      `${this.restServer}/metadata_repository/source/ids_by_study/${id}/`
    )
  }

  getSourceNamesByStudy(id: number): Observable<Source[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/source/names_by_study/${id}/?page_size=20000`
    ).pipe(map((result) => <Source[]>result.results))
  }

  updateSources(sources: Source[], studyId: number): Observable<Source[]> {
    if (sources.length) {
      return this.httpClient.post(
        `${this.restServer}/metadata_repository/source/batch_update_by_study/?study_id=${studyId}`,
        sources
      ).pipe(map((result) => <Source[]>result))
    } else {
      return of([]);
    }
  }

  updateSourcesFromFile(fileName: string, studyId: number, deleteExisting: boolean): Observable<string> {
    // NOTE: body not used... maybe this should be something other than a POST?
    const deleteExistingArg = deleteExisting ? '&delete_existing=true' : '';
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/source/batch_update_by_study_from_file/?study_id=${studyId}`
           + `&file_name=${fileName}${deleteExistingArg}`,
      [],
      { headers: { 'skip-error-interceptor': 'true' }}
    ).pipe(map((result) => <string>result));
  }

  removeSourcesFromStudy(source_ids: number[], study_id: number) {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/source/remove_from_study/`,
      { source_ids: source_ids, study_id: study_id }
    );
  }
  
  updateStudyProtocols(protocol_ids: (number | undefined)[], studyId: number): Observable<object> {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/study/update_protocols/?study_id=${studyId}`,
      protocol_ids
    );
  }

  getProtocolCount(): Observable<number> {
    return this.httpClient.get<number>(
      `${this.restServer}/metadata_repository/protocol/count/`
    );
  }
  
  updateStudyFactors(studyFactors: StudyFactor[], studyId: number): Observable<object> {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/study/update_study_factors/?study_id=${studyId}`,
      studyFactors
    );
  }

  getProcessSequenceByAssay(id: number): Observable<Process[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/process/by_assay/${id}/`
    ).pipe(map((result) => <Process[]>result.results))
  }

  getPagedProcessSequenceByAssay(id: number, pageNumber: number, page_size: number): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/metadata_repository/process/by_assay/${id}/?page=${pageNumber}&page_size=${page_size}`)
      .pipe(map(result => <PagedResponse>result));
  }

  getProcess(id: number): Observable<Process> {
    return this.httpClient.get<Process>(
      `${this.restServer}/metadata_repository/process/${id}/`
    );
  }

  getProcessParameterValue(id: number): Observable<ProcessParameterValue> {
    return this.httpClient.get<ProcessParameterValue>(
      `${this.restServer}/metadata_repository/process_parameter_value/${id}/`
    );
  }

  getOntologyAnnotation(id: number): Observable<OntologyAnnotation> {
    return this.httpClient.get<OntologyAnnotation>(
      `${this.restServer}/ontology_service/ontology_annotation/${id}/`
    );
  }

  getOntologySources(): Observable<OntologySourceReference[]> {
    // TODO: Cache results
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/ontology_service/ontology_source_reference/?page_size=100`
    ).pipe(map((result) => <OntologySourceReference[]>result.results));
  }

  getOntologySource(id: number): Observable<OntologySourceReference> {
    return this.httpClient.get<OntologySourceReference>(
      `${this.restServer}/ontology_service/ontology_source_reference/${id}/`
    );
  }

  getOntologyTermsByType(annotationType: string): Observable<OntologyAnnotation[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/ontology_service/ontology_annotation/?term_type=${annotationType}&page_size=5000`
    ).pipe(map((result) => <OntologyAnnotation[]>result.results));
  }

  getMaterialAttribute(id: number): Observable<MaterialAttribute> {
    return this.httpClient.get<MaterialAttribute>(
      `${this.restServer}/metadata_repository/material_attribute/${id}/`
    ); 
  }

  getMaterialAttributeValue(id: number): Observable<MaterialAttributeValue> {
    return this.httpClient.get<MaterialAttributeValue>(
      `${this.restServer}/metadata_repository/material_attribute_value/${id}/`
    );
  }

  getCharacteristicCategories(): Observable<MaterialAttribute[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/material_attribute/?page_size=500`
    ).pipe(map((result) => <MaterialAttribute[]>result.results));
  }

  getFactors(): Observable<MaterialAttribute[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/material_attribute/?page_size=500`
    ).pipe(map((result) => <MaterialAttribute[]>result.results));
  }

  getSearchExamples(): Observable<SearchTerm[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/core_application/search_example/`
    ).pipe(map((result) => <SearchTerm[]>result.results));
  }

  getFilterTerms(): Observable<FilterTerm[]> {
    return this.httpClient.get<FilterTerm[]>(
      `${this.restServer}/ontology_service/controlled_term/get_filter_terms/`
    )
  }

  getCenters(): Observable<Center[]> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/center/?page_size=500`
    ).pipe(map((result) => <Center[]>result.results));
  }

  addCenter(center: Center): Observable<Center> {
    return this.httpClient.post<Center>(
      `${this.restServer}/metadata_repository/center/`,
      center
    );
  }

  updateCenter(center: Center): Observable<Center> {
    return this.httpClient.put<Center>(
      `${this.restServer}/metadata_repository/center/${center.id}/`,
      center
    );
  }

  deleteCenter(centerId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.restServer}/metadata_repository/center/${centerId}/`
    );
  }

  generatePhenotypeData(dataId: number, assayId: number, existingMeasuresAction = 'update', duplicateMeasuresAction = 'skip'): Observable<unknown> {
    return this.httpClient.post(
      `${this.restServer}/metadata_repository/data/generate_phenotype_data/`,
      {
        data_id: dataId,
        assay_id: assayId,
        existing_measures_action: existingMeasuresAction,
        duplicate_measures_action: duplicateMeasuresAction
      },
      { headers: { 'skip-error-interceptor': 'true' } }
    );
  }
}
